/**

 * @vuelto-surface

 * mobile: ok

 * desktop: ok

 * strategy: inherit

 */

import React from 'react';

import { TicketListRow } from '../../shared/ticket/TicketListRow';

import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';

import type { ExpenseRow } from '../domain/expenseTypes';

import { expenseErpCopy } from '../domain/expenseErpCopy';

import { todayIsoDate } from '../infra/expenseListSql';

import {
  expenseAmountResidual,
  expensePaymentStatusLabel,
  formatExpenseDateShort,
  formatExpenseMoney,
} from '../domain/expenseLabels';

type Props = {
  item: ExpenseRow;
  onOpenDetail: (item: ExpenseRow) => void;
};

function ExpenseRowCardInner({ item, onOpenDetail }: Props) {
  const { variant } = useOperationalFilterLayout();
  const isMobile = variant === 'mobile';

  const residual = expenseAmountResidual(item.amount, item.amountPaid);
  const title = item.supplierName ?? item.categoryName ?? item.categorySlug ?? '—';
  const isVoided = item.status === 'voided';
  const isOverdue =
    !isVoided && item.dueDate != null && item.paymentStatus !== 'paid' && item.dueDate < todayIsoDate();
  const trailingPills = isOverdue ? [{ label: 'Vencida', tone: 'bad' as const }] : undefined;
  const statusLabel = isVoided ? expenseErpCopy.statusVoided : expensePaymentStatusLabel(item.paymentStatus);
  const statusTone = isVoided
    ? 'muted'
    : item.paymentStatus === 'paid'
      ? 'ok'
      : item.paymentStatus === 'partial'
        ? 'warn'
        : 'muted';

  if (isMobile) {
    // Proto mobile: título = saldo (o total), una meta "Nombre · vencida/vence/fecha".
    const duePart = isVoided
      ? null
      : item.dueDate
        ? `${isOverdue ? 'vencida' : 'vence'} ${formatExpenseDateShort(item.dueDate)}`
        : formatExpenseDateShort(item.expenseDate);
    const meta = [title, item.description, duePart, isVoided && item.voidReason ? `Anulada: ${item.voidReason}` : null]
      .filter(Boolean)
      .join(' · ');
    return (
      <TicketListRow
        title={formatExpenseMoney(residual > 0 && !isVoided ? residual : item.amount)}
        meta={meta}
        statusLabel={statusLabel}
        statusTone={statusTone}
        trailingPills={trailingPills}
        inactive={isVoided}
        onPress={() => onOpenDetail(item)}
        accessibilityLabel={`${title}, ${formatExpenseMoney(item.amount)}, ${statusLabel}`}
        testID={`gastos.row.${item.id}`}
      />
    );
  }

  // Desk — misma meta que ExpensesTable.
  const metaParts: string[] = [];
  if (item.supplierName) {
    if (item.categoryName) metaParts.push(item.categoryName);
  } else {
    metaParts.push('Sin proveedor');
  }
  if (item.description) metaParts.push(item.description);
  if (item.receiptRef) metaParts.push(`Ref. ${item.receiptRef}`);
  if (!isVoided && item.dueDate) {
    metaParts.push(isOverdue ? `Vencida ${formatExpenseDateShort(item.dueDate)}` : `Vence ${formatExpenseDateShort(item.dueDate)}`);
  } else {
    metaParts.push(formatExpenseDateShort(item.expenseDate));
  }
  if (residual > 0 && !isVoided) {
    metaParts.push(`Saldo ${formatExpenseMoney(residual)}`);
  }
  if (isVoided && item.voidReason) metaParts.push(`Anulada: ${item.voidReason}`);

  return (
    <TicketListRow
      title={title}
      meta={metaParts.join(' · ') || null}
      value={
        residual > 0 && !isVoided
          ? formatExpenseMoney(residual)
          : formatExpenseMoney(item.amount)
      }
      valueTone={
        isVoided ? null : isOverdue ? 'bad' : residual > 0 && item.dueDate ? 'warn' : null
      }
      valueSub={
        !isVoided && item.amountPaid > 0 && residual > 0
          ? `de ${formatExpenseMoney(item.amount)}`
          : null
      }
      statusLabel={statusLabel}
      statusTone={statusTone}
      trailingPills={trailingPills}
      inactive={isVoided}
      onPress={() => onOpenDetail(item)}
      accessibilityLabel={`${title}, ${formatExpenseMoney(item.amount)}, ${statusLabel}`}
      testID={`gastos.row.${item.id}`}
    />
  );
}

export const ExpenseRowCard = React.memo(ExpenseRowCardInner);
